<template>
    <v-container fluid>
        <template v-if="isAuthorized">
            <v-tabs
                v-model="stepOpener"
                variant="pills"
                density="compact"
                class="rounded"
                style="background: #f4f4f4"
            >
                <!-- Extournes Tab -->
                <v-tab value="2" data-cy="AccountingPeriodOpening-extournes"> Extournes </v-tab>
                <!-- Analyse des pièces hors FEC Tab -->
                <v-tab value="3" data-cy="AccountingPeriodOpening-analyse">
                    Analyse des pièces hors FEC
                </v-tab>
            </v-tabs>

            <!-- Tab Content -->
            <div v-if="stepOpener === '2'" class="tab-content">
                <v-card class="elevation-0" style="background: transparent">
                    <template v-if="accountingPeriods.length === 0">
                        <h1>Pas d'exercices enregistrés</h1>
                    </template>
                    <template v-else-if="!accountingPeriodPrevious">
                        <h1 class="pa-2">N-1 n'a pas été créé dans bobbee</h1>
                        <h2 class="pt-4 px-2 pb-2">Si il existe, il faut :</h2>
                        <h3 class="pa-2">- le créer</h3>
                        <h3 class="pa-2">
                            - importer le FEC (indispensable à la reprise automatique des AN)
                        </h3>
                        <h3 class="pa-2">- le clôturer</h3>
                    </template>
                    <CheckReversal
                        v-else-if="accountingPeriodPrevious && accountingPeriodPrevious.closed"
                        :companyId="companyId"
                        :accountingPeriod="accountingPeriod"
                        :accountingPeriodId="accountingPeriodId"
                    />
                    <h1 v-else>L'exercice N-1 n'est pas clôturé</h1>
                </v-card>
            </div>

            <div v-if="stepOpener === '3'" class="tab-content">
                <v-card class="elevation-0" style="background: transparent">
                    <v-card-text>
                        <h1 class="mb-1">
                            Liste des pièces hors FEC, sur la période liée à l'import FEC
                            sélectionné
                        </h1>
                        <v-row>
                            <v-col cols="12">
                                <CheckDoublonsWithAccountingItemImport :companyId="companyId" />
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </div>
        </template>
    </v-container>
</template>

<script lang="ts">
import { State2Way } from "@/decorators";

import moment from "moment-timezone";
import { Apollo } from "@/decorators";
import { User } from "../store/store";

import SelectUser from "./user/SelectUser.vue";

// ++ opening
import CheckReversal from "./audit/CheckReversal.vue";
import CheckDoublonsWithAccountingItemImport from "./audit/CheckDoublonsWithAccountingItemImport.vue";

// ++ closure
import CheckReceivablesAgeing from "./audit/CheckReceivablesAgeing.vue";
import CheckFixedAssetsToDepreciate from "./audit/CheckFixedAssetsToDepreciate.vue";

import UPSERT_AUDITCHECK_RESULT from "../graphql/audit/UpsertAuditCheckResultAccountingPeriod.gql";
import AUDIT_CHECK_RESULT from "../graphql/audit/AuditCheckResultFromAccountingPeriod.gql";
import { getEmptyDocument } from "../services/documentServices";
import { checkAccessRights } from "../services/accessRightServices";
import { Component, Vue, toNative } from "vue-facing-decorator";

@Component({
    components: {
        SelectUser,
        CheckReversal,
        CheckDoublonsWithAccountingItemImport,
        CheckReceivablesAgeing,
        CheckFixedAssetsToDepreciate,
    },
})
export class AccountingPeriodOpening extends Vue {
    step = 0;
    stepOpener = "2";

    managementStepper = 1;
    id = "";
    managementDocument = null;
    managementLoading = false;
    payAmounts = [
        { amount: 0.0 },
        { amount: 0.0 },
        { amount: 0.0 },
        { amount: 0.0 },
        { amount: 0.0 },
        { amount: 0.0 },
        { amount: 0.0 },
        { amount: 0.0 },
        { amount: 0.0 },
        { amount: 0.0 },
        { amount: 0.0 },
        { amount: 0.0 },
    ];

    depreciationStepper = 1;
    depreciationId = "";
    depreciationDocument = null;
    depreciationLoading = false;

    @State2Way("updateAccountingPeriods", "accountingPeriod.accountingPeriods")
    accountingPeriods!: any;

    @State2Way("updateSelectedAccountingPeriod", "accountingPeriod.selectedAccountingPeriod")
    accountingPeriod: any;

    get currentUser(): User {
        return this.$store.state.user;
    }

    get isAuthorized() {
        const authorized = checkAccessRights(this.currentUser, "accounting", "write");
        return authorized;
    }

    get accountingPeriodId() {
        return this.accountingPeriod ? this.accountingPeriod.id : "";
    }

    get periodStart(): any {
        if (this.accountingPeriod && this.accountingPeriod.start) {
            return moment(this.accountingPeriod.start).format("YYYY-MM-DD");
        }
        return "";
    }

    get periodEnd(): any {
        if (this.accountingPeriod && this.accountingPeriod.end) {
            return moment(this.accountingPeriod.end).format("YYYY-MM-DD");
        }
        return "";
    }

    get accountingPeriodPrevious() {
        if (
            this.accountingPeriods?.length &&
            this.accountingPeriods.length > 1 &&
            this.accountingPeriod?.end
        ) {
            const previousPeriodEnd = moment(this.accountingPeriod.end)
                .add(-1, "years")
                .format("YYYY-MM-DD");

            if (previousPeriodEnd) {
                const accountingPeriodPrevious = this.accountingPeriods.find((ap) =>
                    ap.end.startsWith(previousPeriodEnd),
                );

                return accountingPeriodPrevious || undefined;
            }
        }
        return undefined;
    }

    get companyId() {
        return this.$store.state.selected_company.id;
    }

    depreciationMonths = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

    getDefaultManagementData() {
        const defaultManagement = {
            manager: null,
            amount: 0,
            user: null,
            startDate: "",
            payAmounts: [],
            socialChargeRatio: 0.3,
        };
        return JSON.parse(JSON.stringify(defaultManagement));
    }

    getManagementAuditCheckKey() {
        return "6c69187348394b179c4d2975c";
    }

    getManagementKey() {
        return (
            this.getManagementAuditCheckKey() + "_" + this.companyId + "_" + this.accountingPeriodId
        );
    }

    setManager(value) {
        if (!value) {
            this.data = this.getDefaultManagementData();
            this.fillMonthsAmounts();
        }
        this.data.manager = value;
    }

    @Apollo({
        query: AUDIT_CHECK_RESULT,
        variables() {
            return {
                key: this.getManagementKey(),
            };
        },
        skip() {
            return !this.isAuthorized || !this.companyId || !this.accountingPeriodId;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data && data.auditCheckResult && data.auditCheckResult.id) {
                    this.data = data.auditCheckResult.data;
                    this.payAmounts = [];
                    if (!this.data.socialChargeRatio) {
                        this.data.socialChargeRatio = 0.3;
                    }
                    for (const payAmount of this.data.payAmounts) {
                        this.payAmounts.push({
                            amount: payAmount,
                        });
                    }
                    this.id = data.auditCheckResult.id;
                    this.managementDocument = data.auditCheckResult.results.length
                        ? data.auditCheckResult.results[0]
                        : null;
                    // this.setPeriod()
                } else {
                    this.data = this.getDefaultManagementData();
                    this.id = "";
                    this.managementDocument = null;
                }
            }
        },
    })
    data = {
        manager: null,
        amount: 0,
        user: null,
        startDate: "",
        payAmounts: [],
        socialChargeRatio: 0.3,
    };

    getDepreciationAuditCheckKey() {
        return "1e268adf359e406e87629d6ce";
    }

    getDefaultDepreciationData() {
        const defaultDepreciation = {
            depreciation: null,
            amountSigned: 12000,
            startDate: "",
            mounthCount: 12,
            chargeAccountableAccount: "681120",
            fixedassetsAccountableAccount: "281830",
        };
        return JSON.parse(JSON.stringify(defaultDepreciation));
    }

    getDepreciationKey() {
        return (
            this.getDepreciationAuditCheckKey() +
            "_" +
            this.companyId +
            "_" +
            this.accountingPeriodId
        );
    }

    @Apollo({
        query: AUDIT_CHECK_RESULT,
        variables() {
            return {
                key: this.getDepreciationKey(),
            };
        },
        skip() {
            return !this.isAuthorized || !this.companyId || !this.accountingPeriodId;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data && data.auditCheckResult && data.auditCheckResult.id) {
                    this.depreciationData = data.auditCheckResult.data;
                    this.depreciationId = data.auditCheckResult.id;
                    this.depreciationDocument = data.auditCheckResult.results.length
                        ? data.auditCheckResult.results[0]
                        : null;
                } else {
                    this.depreciationData = this.getDefaultDepreciationData();
                    this.depreciationId = "";
                    this.depreciationDocument = null;
                }
            }
        },
    })
    depreciationData = {
        depreciation: null,
        amountSigned: 12000,
        startDate: "",
        mounthCount: 12,
        chargeAccountableAccount: "681120",
        fixedassetsAccountableAccount: "281830",
    };

    setDepreciation(value) {
        if (!value) {
            this.depreciationData = this.getDefaultDepreciationData();
        }
        this.depreciationData.depreciation = value;
    }

    setAccountingPeriodName(period) {
        const start = moment(period.start).format("DD MMM YYYY");
        const end = moment(period.end).format("DD MMM YYYY");
        return "Du " + start + " au " + end;
    }

    fillMonthsAmounts() {
        for (let i = 0; i < this.payAmounts.length; i++) {
            this.payAmounts[i].amount = this.data.amount / 12;
        }
    }

    getMonthName(index) {
        if (!this.accountingPeriodId) {
            return moment().add(index, "month").format("MMMM");
        }
        const period = this.accountingPeriods.filter((x) => x.id === this.accountingPeriodId)[0];
        return moment(period.start).add(index, "month").format("MMMM");
    }

    async valid() {
        this.managementLoading = true;
        const documents = [];
        if (this.data.manager && this.accountingPeriod) {
            const document: any = getEmptyDocument(
                this.companyId,
                "Provision sur rémunération et cotisations sociales",
                "MISCELLANEOUS_OPERATION",
                this.accountingPeriod.start,
                this.data.user.id,
            );
            this.data.payAmounts = this.payAmounts.map((pa) => pa.amount);
            this.data.startDate = this.accountingPeriod.start;
            document.data = {
                selfEmployedPay: this.data,
            };
            documents.push(document);
        }

        const auditCheckResultVariables: any = {
            id: this.id,
            auditCheckKey: this.getManagementAuditCheckKey(),
            companyId: this.companyId,
            accountingPeriodId: this.accountingPeriodId,
            key: this.getManagementKey(),
            data: this.data,
            deleteDocuments: [],
            documents: documents,
            status: "UNKNOWN",
        };
        if (this.managementDocument) {
            auditCheckResultVariables.deleteDocuments = [this.managementDocument.id];
        }
        debugger;
        const result = await this.$apollo.mutate({
            mutation: UPSERT_AUDITCHECK_RESULT,
            variables: auditCheckResultVariables,
        });

        if (result && result.data && result.data.upsertAuditCheckResult) {
            this.id = result.data.upsertAuditCheckResult.id;
            this.managementDocument = result.data.upsertAuditCheckResult.results.length
                ? result.data.upsertAuditCheckResult.results[0]
                : null;
        }
        this.managementLoading = false;
        debugger;
    }

    async depreciationValid() {
        this.depreciationLoading = true;
        if (this.depreciationData.depreciation != null && this.accountingPeriod) {
            const documents = [];
            if (this.depreciationData.depreciation) {
                const depreciationDocument: any = getEmptyDocument(
                    this.companyId,
                    "Provision sur amortissement",
                    "MISCELLANEOUS_OPERATION",
                    moment(this.accountingPeriod.start).endOf("month").startOf("day"),
                    undefined,
                );
                this.depreciationData.startDate = this.accountingPeriod.start;
                depreciationDocument.data = {
                    depreciation: this.depreciationData,
                };
                documents.push(depreciationDocument);
            }

            debugger;

            const auditCheckResultDepreciationVariables: any = {
                id: this.depreciationId,
                auditCheckKey: this.getDepreciationAuditCheckKey(),
                companyId: this.companyId,
                accountingPeriodId: this.accountingPeriodId,
                key: this.getDepreciationKey(),
                data: this.depreciationData,
                deleteDocuments: [],
                documents: documents,
                status: "UNKNOWN",
            };
            if (this.depreciationDocument) {
                auditCheckResultDepreciationVariables.deleteDocuments = [
                    this.depreciationDocument.id,
                ];
            }

            const result = await this.$apollo.mutate({
                mutation: UPSERT_AUDITCHECK_RESULT,
                variables: auditCheckResultDepreciationVariables,
            });
            if (result && result.data && result.data.upsertAuditCheckResult) {
                this.depreciationId = result.data.upsertAuditCheckResult.id;
                this.depreciationDocument = result.data.upsertAuditCheckResult.results.length
                    ? result.data.upsertAuditCheckResult.results[0]
                    : null;
            }
        } else {
            debugger;
        }
        this.depreciationLoading = false;
    }

    getManagerClass(value) {
        let managerClass = "oss-list-item mb-2";
        if (value === this.data.manager) managerClass += " oss-list-item-selected";
        return managerClass;
    }

    getDepreciationClass(value) {
        let depreciationClass = "oss-list-item mb-2";
        if (value === this.depreciationData.depreciation)
            depreciationClass += " oss-list-item-selected";
        return depreciationClass;
    }
}

export default toNative(AccountingPeriodOpening);
</script>
